/**
 * TeamRulesPanel — the game menu's "Team play rules" modal (team games only;
 * maintainer, 2026-11-03). Your team at the top (its mark, label and members
 * in their colours), then the rules: the same coloured rows, icons and footer
 * chips the map legend draws (lib/ruleOverlayModel buildTeamRulesModel,
 * rendered by RulesReadOnlyList), as a plain read-only list.
 */
import React from 'react'
import { ScrollView, Text, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { teamLabel } from '@warcarp/api-types'
import type { TeamRulesInfo } from '../../lib/ruleOverlayModel'
import { overlayPalette } from '../../lib/rulesOverlayTheme'
import { logNameColor } from '../../lib/playerTextColor'
import { useTheme } from '../../hooks/useTheme'
import { RulesReadOnlyList } from '../map/rules/RulesOverlayLegend'
import { TeamShape } from './TeamShape'
import { FloatingGamePanel } from './FloatingGamePanel'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'

/** The panel's heading, drawn on the ×'s strip (FloatingGamePanel `title`) so it costs no row. */
export function TeamRulesTitle() {
  const { theme } = useTheme()
  const palette = overlayPalette(theme)
  return (
    <View testID="team-rules-title" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <MaterialCommunityIcons name="account-group" size={20} color={palette.ink} accessible={false} />
      <Text accessibilityRole="header" className="font-stencil text-ink uppercase" style={{ fontSize: 16 }} numberOfLines={1}>Team play rules</Text>
    </View>
  )
}

export function TeamRulesPanel({ rules }: { rules: TeamRulesInfo }) {
  const { theme } = useTheme()
  const palette = overlayPalette(theme)
  const textTheme = theme === 'dark' ? 'dark' : 'light'
  const label = teamLabel(rules.team)
  return (
    <ScrollView testID="team-rules-modal" contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 14, paddingTop: 6, gap: 12 }}>
      <View
        testID="team-rules-your-team"
        accessible
        accessibilityLabel={`Your team: ${label}, ${rules.members.map((m) => (m.you ? `${m.name} (you)` : m.name)).join(', ')}`}
        style={{ borderWidth: 1.5, borderColor: palette.olive, borderRadius: 12, backgroundColor: palette.paperUp, paddingHorizontal: 12, paddingVertical: 10, gap: 4 }}
      >
        <Text className="font-stencil text-ink-soft uppercase" style={{ fontSize: 10, letterSpacing: 0.5 }}>Your team</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <TeamShape team={rules.team} size={18} fill={rules.members.find((m) => m.you)?.color ?? palette.olive} />
          <Text className="font-stencil text-ink" style={{ fontSize: 15 }}>{label}</Text>
        </View>
        <Text className="font-typed" style={{ fontSize: 14, lineHeight: 19 }}>
          {rules.members.map((m, i) => (
            <Text key={m.id}>
              {i > 0 ? <Text style={{ color: palette.inkSoft }}> + </Text> : null}
              <Text style={{ color: logNameColor(m.color, textTheme), fontWeight: '700' }}>{m.name}</Text>
              {m.you ? <Text style={{ color: palette.inkSoft }}> (you)</Text> : null}
            </Text>
          ))}
        </Text>
      </View>
      <RulesReadOnlyList model={rules.model} palette={palette} />
    </ScrollView>
  )
}

/**
 * The team play rules on their own, in the same floating panel as the game
 * menu's: opened by itself the first time a player enters a team game
 * (lib/teamRulesNotice).
 */
export function TeamRulesModal({ visible, onClose, rules }: { visible: boolean; onClose: () => void; rules: TeamRulesInfo }) {
  useWebModalHistory({ visible, canDismiss: true, onDismiss: onClose })
  return (
    <FloatingGamePanel visible={visible} onClose={onClose} closeLabel="Close team play rules" closeTestID="team-rules-close" title={<TeamRulesTitle />}>
      <TeamRulesPanel rules={rules} />
    </FloatingGamePanel>
  )
}
